import React from 'react';
import { Formik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeTimes } from '@uplift/api';
import { CalendarDateService, DateObject } from '@uplift/services';
import { AvailabilityFormModal, AvailabilityFormModalProps, TimeRangeInputValue } from '@uplift/ui-v2';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { ProviderMedicalRole, TimeRange } from '@uplift/types';
import { useAvailabilityOverrideData } from './AvailabilityOverride.hooks';


export type AvailabilityOverrideModalProps = {
  open: boolean;
  onClose: () => void;
  prepend: AvailabilityFormModalProps['prepend'];
  onAvailabilityDateOverride: (date: DateObject, ranges: TimeRange[]) => Promise<void>;
};


export const AvailabilityOverrideModal: React.FC<AvailabilityOverrideModalProps> = ({
  open,
  onClose,
  prepend,
  onAvailabilityDateOverride,
}) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const queryClient = useQueryClient();

  const today = React.useMemo(() => {
    return CalendarDateService.date().tz(timezone).startOf('day');
  }, [ timezone ]);
  const [ date, setDate ] = React.useState<DateObject>();
  const currentDate = date || today;
  const { data, isInitialLoading: isLoading } = useAvailabilityOverrideData(me.id, currentDate, open);
  const isTemplate = data?.currentRanges === data?.defaultRanges;

  const handleSubmit = React.useCallback(async (values: { availability: TimeRangeInputValue[] }) => {
    const dayRanges = values.availability.map(r => ({ start: r.start, end: r.end }));
    const normalizedDayRanges = await normalizeTimes({ ranges: dayRanges, squash: true });

    await onAvailabilityDateOverride(currentDate, normalizedDayRanges.ranges);
    queryClient.setQueryData([ 'availabilityTemplate', me.id, currentDate ], {
      currentRanges: normalizedDayRanges.ranges,
      defaultRanges: data?.defaultRanges,
    });
    onClose();
  }, [ onClose, onAvailabilityDateOverride, queryClient, data, currentDate, me ]);

  return (
    <Formik
      enableReinitialize
      onSubmit={handleSubmit}
      initialValues={{
        availability: data?.currentRanges?.map((a, i) => ({ ...a, id: i })) ?? []
      }}
    >
      <AvailabilityFormModal
        isAddingOverride={isTemplate}
        handleClosed={() => setDate(undefined)}
        prepend={prepend}
        loading={isLoading}
        open={open}
        title="Pick a Date to Override"
        date={currentDate}
        onDateSelect={setDate}
        defaultAvailability={data?.defaultRanges ?? []}
        handleClose={onClose}
        warningOnLessThenHour={me.medicalRole === ProviderMedicalRole.therapist}
      />
    </Formik>
  );
};
